import WarningIcon from '@mui/icons-material/Warning'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import { useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useMutation, useQuery } from '@tanstack/react-query'
import KNCaption from 'components/KNCaption/KNCaption'
import KNDialog from 'components/KNDialog/KNDialog'
import KNDialogFormErrors from 'components/KNDialog/KNDialogFormErrors'
import { getRouteName } from 'global/helpers/activeRoute'
import { analyticsEvent } from 'global/helpers/analytics'
import { processServerErrorMessages } from 'global/helpers/form'
import { type ReactElement, useEffect, useState } from 'react'
import { FormProvider, type SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import { TRIPS_QUERY_KEY } from 'screens/TripDashboard/TripDashboard.service'
import type { TripData } from 'screens/TripDashboard/TripDashboard.types'
import { TRIP_DETAILS_QUERY_KEY, tripLegsQueryOptions, tripLogsQueryOptions } from 'screens/TripDetails/TripDetails.service'
import { type AvailableStopStatus, type LegData, type StopData, WorkflowType } from 'screens/TripDetails/TripDetails.types'
import DocumentsUploadFields from './DocumentsUploadFields'
import {
  getDefaultStatus,
  getDefaultStatusDate,
  getDefaultStatusTime,
  getTimezoneReference,
  getTruckZoneMessage,
  getUpdatedStopForUpdateStopStatusWithAttachments,
  updateTripLegsCacheWithUpdatedStops,
} from './StatusManager.helpers'
import { updateStopStatusWithAttachments } from './StatusManager.service'
import type { StatusUpdateWithAttachmentsFormValues } from './StatusManager.types'
import { getStatusUpdateLabel } from './StatusUpdateDialog'
import StatusUpdateFields from './StatusUpdateFields'

interface StatusUpdateWithAttachmentsDialogPayload {
  trip: TripData
  leg: LegData
  stop: StopData
  weblinkToken?: string
  noRegularStatuses?: boolean
  noDiscrepancyStatuses?: boolean
}

interface StatusUpdateWithAttachmentsDialogProps {
  payload: StatusUpdateWithAttachmentsDialogPayload
  open: boolean
  onAction: () => void
  onClose: () => void
}

const StatusUpdateWithAttachmentsDialog = ({
  payload,
  open,
  onAction,
  onClose,
}: StatusUpdateWithAttachmentsDialogProps): ReactElement => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const { t } = useTranslation()
  const location = useLocation()
  const [availableStatuses, setAvailableStatuses] = useState<AvailableStopStatus[]>([])
  const [useDeviceTime, setUseDeviceTime] = useState<boolean>()
  const [pendingFiles, setPendingFiles] = useState(false)
  const form = useForm<StatusUpdateWithAttachmentsFormValues>({
    criteriaMode: 'all',
  })
  const { handleSubmit, watch, reset, formState, setError } = form
  const watchedStatusId = watch('statusId')
  const [selectedStatus, setSelectedStatus] = useState<AvailableStopStatus>()
  const tripLogsQuery = useQuery({
    ...tripLogsQueryOptions(payload.trip.entityId, payload.weblinkToken),
    enabled: open,
  })
  const truckZoneMessage = getTruckZoneMessage(
    tripLogsQuery.data ?? [],
    payload.stop.type,
    payload.stop.wayPointCid,
  ) ?? undefined
  const updateStatusMutation = useMutation({
    mutationFn: (data: StatusUpdateWithAttachmentsFormValues) =>
      updateStopStatusWithAttachments(
        payload.trip.entityId,
        payload.stop.wayPointCid,
        data,
        payload.weblinkToken,
      ),
    onSuccess: async (_response, data, _result, context) => {
      const updatedStop = getUpdatedStopForUpdateStopStatusWithAttachments(payload.stop, data)
      const queryKey = tripLegsQueryOptions(payload.trip.entityId, payload.weblinkToken).queryKey
      await updateTripLegsCacheWithUpdatedStops([updatedStop], queryKey, context.client)
      setTimeout(() => {
        void Promise.all([
          context.client.invalidateQueries({ queryKey: [TRIP_DETAILS_QUERY_KEY, payload.trip.entityId] }),
          context.client.invalidateQueries({ queryKey: [TRIPS_QUERY_KEY] }),
        ])
      }, 2000)
      onAction()
      analyticsEvent('polestar_cs_status_with_attachments_updated', [
        getRouteName(location.pathname),
        data.paperless ? 'paperless' : 'regular',
      ])
    },
    onError: (error) => setError('root', processServerErrorMessages(error)),
  })

  useEffect(() => {
    setUseDeviceTime(payload.leg.workflowType !== WorkflowType.SECURE_CLOUD_DEPLOYMENT && isMobile && !!payload.weblinkToken)
  }, [payload, isMobile])

  useEffect(() => {
    setSelectedStatus(availableStatuses.find((status) => status.id === watchedStatusId))
  }, [watchedStatusId, availableStatuses])

  useEffect(() => {
    const availableStatuses = [
      ...(!payload.noRegularStatuses ? payload.stop.availableRegularStatuses : []),
      ...(!payload.noDiscrepancyStatuses ? payload.stop.availableDiscrepancyStatuses : []),
    ]
    setAvailableStatuses(availableStatuses)
    reset({
      wayPointType: payload.stop.type,
      statusId: getDefaultStatus(availableStatuses),
      createdAtDate: getDefaultStatusDate(payload.stop, payload.leg),
      createdAtTime: getDefaultStatusTime(payload.stop, payload.leg),
      shipmentNumber: payload.leg.shipmentNumber,
      paperless: isMobile && Boolean(payload.weblinkToken) && payload.leg.paperlessAvailable && !payload.stop.isProofAttached,
    })
  }, [payload])

  const onSubmit: SubmitHandler<StatusUpdateWithAttachmentsFormValues> = (
    data: StatusUpdateWithAttachmentsFormValues,
  ) => {
    if (useDeviceTime) {
      data.createdAt = undefined
    }
    updateStatusMutation.mutate(data)
  }

  return (
    <FormProvider {...form}>
      <KNDialog
        open={open}
        onClose={onClose}
        closeLabel={t('general.cancel')}
        preventClosing={formState.isSubmitting || updateStatusMutation.isPending}
        title={`${payload.leg.shipmentNumber} — ${getStatusUpdateLabel(payload.stop.type)}`}
        actions={
          <Button
            type="submit"
            color="secondary"
            variant="contained"
            loading={formState.isSubmitting || updateStatusMutation.isPending || pendingFiles}
            onClick={handleSubmit(onSubmit)}
          >
            {t('screens.cs.trip_details.update_status.update_status')}
          </Button>
        }
        onSubmit={handleSubmit(onSubmit)}
      >
        <KNDialogFormErrors errors={formState.errors?.root} />
        <Stack spacing={2}>
          <StatusUpdateFields
            availableStatuses={availableStatuses}
            hasProof={payload.stop.isProofAttached}
            showPaperless={payload.leg.paperlessAvailable}
            truckZoneMessage={truckZoneMessage}
            timezoneReference={getTimezoneReference(payload.stop, payload.leg)}
            useDeviceTime={useDeviceTime}
            usePreciseTime={payload.leg.workflowType === WorkflowType.SECURE_CLOUD_DEPLOYMENT}
            weblinkToken={payload.weblinkToken}
          />
          <DocumentsUploadFields
            stopType={payload.stop.type}
            showProof={!!selectedStatus && selectedStatus.proofs !== 'DISABLED'}
            hasProof={payload.stop.isProofAttached}
            showPhotos={!!selectedStatus && selectedStatus.attachments !== 'DISABLED'}
            showPalletsDocument={payload.stop.palletsDocumentRequired}
            hasPalletsDocument={payload.stop.palletsDocumentAttached}
            showTro={payload.leg.troAvailable}
            hasTro={payload.stop.documents?.find((document) => document.fileType === 'TRO') !== undefined}
            onPending={setPendingFiles}
            weblinkToken={payload.weblinkToken}
          />
          <KNCaption iconColor="warning" icon={<WarningIcon />}>
            {t('screens.cs.trip_details.update_status.warning')}
          </KNCaption>
        </Stack>
      </KNDialog>
    </FormProvider>
  )
}

export default StatusUpdateWithAttachmentsDialog
